<template>
  <div class="container">
    <div class="p-voucher__inner-padding">
      <div class="p-voucher__left-col">
        <div class="p-voucher__title-box">
          <div class="p-voucher__title-box__title">{{ dictionary.title }}</div>
          <div
            class="p-voucher__title-box__text"
            v-html="dictionary.text"></div>
          <div class="p-voucher__title-box__desc">
            {{ dictionary.note }}
          </div>
        </div>
      </div>
      <div class="p-voucher__right-col">
        <Form v-slot="{ meta, errors }" @submit="submitVoucherData">
          <Loader v-if="isLoading"></Loader>
          <div class="p-voucher__form-container">
            <div class="p-voucher__form">
              <Alert
                v-if="!isLoading && errorMsgs.length"
                key="submit-errors"
                dismiss-by-native=""
                type="danger"
                :messages="errorMsgs"
                @close="removeErrors"></Alert>
              <div class="form-floating">
                <Field
                  v-model="emailValue"
                  class="form-control"
                  :class="{
                    'form-control--error': errors.email && errors.email.length,
                  }"
                  name="email"
                  type="email"
                  :placeholder="dictionary.email"
                  rules="required|email"
                  :validate-on-change="false" />
                <label for="email">{{ dictionary.email }}</label>
                <ErrorMessage v-slot="{ message }" name="email">
                  <div class="field-validation-error">
                    {{ message }}
                  </div>
                </ErrorMessage>
              </div>
              <div class="form-floating">
                <Field
                  v-slot="{ value }"
                  v-model="cinemaValue"
                  class="form-select"
                  :class="{
                    'form-select--error': errors.cinema && errors.cinema.length,
                  }"
                  name="cinema"
                  as="select"
                  rules="required">
                  <option value="" disabled>
                    {{ dictionary.selectCinema }}
                  </option>
                  <option
                    v-for="cinema in cinemas"
                    :key="cinema.id"
                    :value="cinema.id"
                    :selected="value">
                    {{ cinema.text }}
                  </option>
                </Field>
                <label for="cinema">{{ dictionary.cinema }}</label>
                <ErrorMessage v-slot="{ message }" name="cinema">
                  <div class="field-validation-error">
                    {{ message }}
                  </div>
                </ErrorMessage>
              </div>
              <div class="form-floating">
                <Field
                  v-model="voucherValue"
                  class="form-control"
                  :class="{
                    'form-control--error':
                      errors.voucher && errors.voucher.length,
                  }"
                  name="voucher"
                  type="text"
                  :placeholder="dictionary.voucher"
                  rules="required"
                  :validate-on-change="false" />
                <label for="cinema">{{ dictionary.voucher }}</label>
                <ErrorMessage v-slot="{ message }" name="voucher">
                  <div class="field-validation-error">
                    {{ message }}
                  </div>
                </ErrorMessage>
              </div>
              <div class="p-voucher__form-message">
                {{ dictionary.required }}
              </div>
            </div>
            <div class="p-voucher__form-btn">
              <button
                type="submit"
                class="p-voucher__submit-btn"
                :disabled="!meta.valid">
                <span>{{ dictionary.submitCta }}</span>
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
    <ModalOverlay
      v-model="showActionModal"
      background-class="b-pop-up--alert-success b-pop-up__holder"
      header-background-class="b-pop-up__header">
      <template #header>
        <div class="b-pop-up__header__title">{{ dictionary.title }}</div>
      </template>
      <template #body>
        <div class="b-pop-up__content">
          <div
            class="b-pop-up__alert-success__icon"
            :class="modalSubmitStatus"></div>
          <div class="b-pop-up__alert-success__content__container">
            <div class="b-pop-up__alert-success__title">
              {{ modalSubmitStatusMessage }}
            </div>
            <div v-if="!submitFail" class="b-pop-up__alert-success__text">
              {{ isZeroCreditNote ? '' : dictionary.modalDesc }}
            </div>
            <button
              type="button"
              class="b-pop-up__alert-success__cta"
              @click="() => (showActionModal = false)">
              {{ dictionary.close }}
            </button>
          </div>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'
import { getData, getLocale, getUrlParam, scrollToError } from '../../lib/lib'
import ModalOverlay from '../components/ModalOverlay.vue'
import Loader from './../components/Shared/Loader.vue'
import Alert from '../components/Shared/Alert.vue'
import { ErrorMessage, Field, Form, configure, defineRule } from 'vee-validate'
import { email, required } from '@vee-validate/rules'
import { localize } from '@vee-validate/i18n'
import postRequestHandler from '../../lib/postRequestHandler'

defineRule('required', required)
defineRule('email', email)

const { dictionary, configuration, cinemas } = getData('createVoucherData')
const isLoading = ref(false)
const errorMsgs = ref([])

const emailValue = ref()
const cinemaValue = ref()
const voucherValue = ref()
const isZeroCreditNote = ref(false)

onMounted(() => {
  configure({
    generateMessage: localize(getLocale(), {
      fields: {
        email: {
          required: dictionary.emailRequired,
          email: dictionary.emailRegex,
        },
        cinema: {
          required: dictionary.cinemaRequired,
        },
        voucher: {
          required: dictionary.reservationCodeRequired,
        },
      },
    }),
  })

  if (getUrlParam('email')) {
    emailValue.value = getUrlParam('email')
  }
  if (
    getUrlParam('cinemaId') &&
    cinemas.find((x) => x.id === getUrlParam('cinemaId'))
  ) {
    cinemaValue.value = getUrlParam('cinemaId')
  }
  if (getUrlParam('bookingId')) {
    voucherValue.value = getUrlParam('bookingId')
  }
})

const showActionModal = ref(false)
const submitFail = ref(false)

const modalSubmitStatus = computed(() => {
  return submitFail.value
    ? 'b-pop-up__alert-success__icon--warning'
    : 'b-pop-up__alert-success__icon--success'
})

const modalSubmitStatusMessage = computed(() => {
  const successMessage = isZeroCreditNote.value
    ? dictionary.sendSuccessZeroCredit
    : dictionary.sendSuccess

  return submitFail.value ? dictionary.sendFail : successMessage
})

const submitVoucherData = async (values, { resetForm }) => {
  isLoading.value = true
  submitFail.value = false
  removeErrors()
  const postData = {
    email: values.email.toLowerCase(),
    cinemaId: values.cinema,
    bookingId: values.voucher,
    language: document.documentElement.lang,
  }
  await postRequestHandler({
    postData,
    endpoint: configuration.submitVoucherHandler,
    successCallback: (response) => {
      isZeroCreditNote.value = response.data.isZeroCreditNote
      showActionModal.value = true
      isLoading.value = false
      resetForm()
    },
    errorCallback: handleError,
    refreshTokenHandler: configuration.refresh,
    captchaAction: configuration.captchaCreateCreditNoteAction,
  })
}

const handleError = (messages) => {
  isLoading.value = false
  if (!messages) {
    errorMsgs.value = [dictionary.defaultErrorMessage]
  } else if (typeof messages === 'string') {
    errorMsgs.value = [messages]
  } else {
    errorMsgs.value = [...messages]
  }

  nextTick(() => {
    scrollToError()
  })
}
const removeErrors = () => {
  errorMsgs.value = []
}
</script>
